import { useState, useEffect, useCallback } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import axios from "axios";
import {
  FileText,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Bot,
  User,
  Shield,
  RefreshCw,
  Download,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_API_URL;

export default function AdminAuditLogs() {
  const { user } = useAdminBoard();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  
  // Filters
  const [category, setCategory] = useState("all");
  const [actorType, setActorType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(50);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/admin-board/audit-logs?limit=${limit}`;
      if (category !== "all") url += `&category=${category}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      
      setLogs(res.data);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [user, category, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = logs.filter(log => {
    if (actorType !== "all" && log.actor_type !== actorType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.action?.toLowerCase().includes(query) ||
        log.event_type?.toLowerCase().includes(query) ||
        log.actor_name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getCategoryColor = (cat) => {
    switch (cat) {
      case "override": return "bg-red-500 text-white";
      case "approval": return "bg-amber-500 text-white";
      case "agent_decision": return "bg-cyan-500 text-white";
      case "call": return "bg-emerald-500 text-white";
      case "config_change": return "bg-purple-500 text-white";
      default: return "bg-slate-400 text-white";
    }
  };

  const getActorIcon = (type) => {
    switch (type) {
      case "ai_agent": return <Bot className="w-4 h-4 text-cyan-600" />;
      case "human_admin":
      case "human_operator": return <User className="w-4 h-4 text-emerald-600" />;
      default: return <Shield className="w-4 h-4 text-slate-500" />;
    }
  };

  const exportLogs = () => {
    const csv = [
      ["Timestamp", "Category", "Event Type", "Actor", "Action", "Business ID"].join(","),
      ...filteredLogs.map(log => [
        new Date(log.timestamp).toISOString(),
        log.category,
        log.event_type,
        `${log.actor_type}:${log.actor_name || log.actor_id}`,
        `"${log.action?.replace(/"/g, '""')}"`,
        log.business_id || ""
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `speakr-audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Audit logs exported");
  };

  return (
    <div className="p-4 lg:p-6 space-y-6 bg-slate-50 min-h-full" data-testid="admin-audit-logs">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Audit Logs</h1>
          <p className="text-slate-500 text-sm mt-1">
            Immutable record of all AI and human actions - Read Only
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchLogs}
            className="border-slate-300"
            data-testid="refresh-logs"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportLogs}
            className="border-slate-300"
            data-testid="export-logs"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search actions, events, actors..."
            className="pl-10 bg-white border-slate-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-logs"
          />
        </div>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-40 bg-white border-slate-300">
            <Filter className="w-4 h-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="call">Calls</SelectItem>
            <SelectItem value="agent_decision">Agent Decisions</SelectItem>
            <SelectItem value="override">Overrides</SelectItem>
            <SelectItem value="approval">Approvals</SelectItem>
            <SelectItem value="config_change">Config Changes</SelectItem>
            <SelectItem value="security">Security</SelectItem>
          </SelectContent>
        </Select>

        <Select value={actorType} onValueChange={setActorType}>
          <SelectTrigger className="w-40 bg-white border-slate-300">
            <SelectValue placeholder="Actor Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actors</SelectItem>
            <SelectItem value="ai_agent">AI Agent</SelectItem>
            <SelectItem value="human_admin">Human Admin</SelectItem>
            <SelectItem value="human_operator">Human Operator</SelectItem>
            <SelectItem value="system">System</SelectItem>
          </SelectContent>
        </Select>

        <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
          <SelectTrigger className="w-28 bg-white border-slate-300">
            <SelectValue placeholder="Limit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="200">200</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs List */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-slate-600" />
            Event Log ({filteredLogs.length} entries)
          </CardTitle>
          <CardDescription className="text-xs">
            Read-only audit trail. Entries cannot be modified or deleted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
              <p className="text-sm">Loading audit logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No audit logs found</p>
              <p className="text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className={`border rounded-lg overflow-hidden transition-all ${
                    log.severity === "critical"
                      ? "border-red-300 bg-red-50"
                      : log.severity === "warning"
                      ? "border-amber-300 bg-amber-50"
                      : "border-slate-200 bg-white"
                  }`}
                  data-testid={`log-${log.id}`}
                >
                  {/* Log Header */}
                  <div
                    className="p-3 flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  >
                    {/* Timestamp */}
                    <div className="w-28 flex-shrink-0">
                      <p className="text-xs text-slate-500">
                        {new Date(log.timestamp).toLocaleDateString()}
                      </p>
                      <p className="text-sm font-mono text-slate-700">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </p>
                    </div>

                    {/* Category Badge */}
                    <Badge className={`${getCategoryColor(log.category)} w-24 justify-center text-xs`}>
                      {log.category?.replace("_", " ")}
                    </Badge>

                    {/* Actor */}
                    <div className="flex items-center gap-2 w-28 flex-shrink-0">
                      {getActorIcon(log.actor_type)}
                      <span className="text-sm text-slate-700 truncate">
                        {log.actor_name || log.actor_id?.slice(0, 8)}
                      </span>
                    </div>

                    {/* Action */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 truncate">{log.action}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {log.event_type}
                      </p>
                    </div>

                    {/* Severity indicator */}
                    {(log.severity === "critical" || log.severity === "warning") && (
                      <AlertTriangle
                        className={`w-4 h-4 flex-shrink-0 ${
                          log.severity === "critical" ? "text-red-600" : "text-amber-600"
                        }`}
                      />
                    )}

                    {/* Expand/Collapse */}
                    {expandedId === log.id ? (
                      <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                  </div>

                  {/* Expanded Details */}
                  {expandedId === log.id && (
                    <div className="p-4 border-t border-slate-200 bg-slate-50 text-sm space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Event ID</p>
                          <p className="font-mono text-xs text-slate-700">{log.id}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Business ID</p>
                          <p className="font-mono text-xs text-slate-700">{log.business_id || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Session ID</p>
                          <p className="font-mono text-xs text-slate-700">{log.session_id || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Correlation ID</p>
                          <p className="font-mono text-xs text-slate-700">{log.correlation_id || "N/A"}</p>
                        </div>
                      </div>

                      {/* Decision Explanation - AI vs Human */}
                      {log.decision_explanation && (
                        <div className="p-3 bg-cyan-50 border border-cyan-200 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <Bot className="w-4 h-4 text-cyan-600" />
                            <p className="text-xs text-cyan-700 font-medium">AI Reasoning</p>
                          </div>
                          <p className="text-slate-700">{log.decision_explanation}</p>
                        </div>
                      )}

                      {/* Confidence Score */}
                      {log.confidence_score && (
                        <div>
                          <p className="text-xs text-slate-500">Confidence Score</p>
                          <p className="text-slate-700 font-medium">{Math.round(log.confidence_score * 100)}%</p>
                        </div>
                      )}

                      {/* Override Linkage - Important for audit trail */}
                      {log.overrides_event_id && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded">
                          <p className="text-xs text-red-700 font-medium">
                            OVERRIDE - This action overrode event:
                          </p>
                          <p className="font-mono text-xs text-red-600">{log.overrides_event_id}</p>
                        </div>
                      )}

                      {/* Inputs/Outputs */}
                      {(log.inputs && Object.keys(log.inputs).length > 0) && (
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Inputs</p>
                          <pre className="text-xs bg-white p-2 rounded overflow-x-auto border border-slate-200 text-slate-700">
                            {JSON.stringify(log.inputs, null, 2)}
                          </pre>
                        </div>
                      )}

                      {(log.outputs && Object.keys(log.outputs).length > 0) && (
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Outputs</p>
                          <pre className="text-xs bg-white p-2 rounded overflow-x-auto border border-slate-200 text-slate-700">
                            {JSON.stringify(log.outputs, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Checksum - Integrity verification */}
                      <div className="pt-2 border-t border-slate-200">
                        <p className="text-xs text-slate-500">
                          Integrity Checksum: <span className="font-mono">{log.checksum?.slice(0, 16) || "N/A"}...</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
